import React, { useEffect, useRef, useState } from 'react'

import { Popover, Tooltip } from '@material-ui/core'
import EventIcon from '@material-ui/icons/Event'
import { DatePicker } from '@material-ui/pickers'
import { DateTime } from 'luxon'

import { TemplateService } from '../../../../../models'
import {
    FIT_SERVICE_ROW_HEIGHT,
    SERVICE_ROW_HEIGHT,
    VENDOR_ROW_HEIGHT,
} from './constants'
import { useWalkScheduleStyles } from './styles'

interface DragState {
    vendorId: number
    edge: 'start' | 'end'
    startIndex: number
    endIndex: number
}

interface DatePickerState {
    vendorId: number
    edge: 'start' | 'end'
    anchor: HTMLElement
    startIndex: number
    endIndex: number
}

interface Props {
    days: DateTime[]
    dayWidth: number
    services: TemplateService[]
    workordersByVendorDate: Map<string, number>
    transferStart: DateTime | null
    transferEnd: DateTime | null
    destReady: DateTime | null
    dtReady: DateTime | null
    dayOffKeys: Set<string>
    zone: string
    compact?: boolean
    onDayClick: (day: DateTime, anchor: HTMLElement) => void
    onVendorRange: (
        vendorId: number,
        startIndex: number,
        endIndex: number,
    ) => void
}

const dayKey = (day: DateTime) => day.toFormat('yyyy-LL-dd')

const covers = (
    day: DateTime,
    start: DateTime | null,
    end: DateTime | null,
) => {
    if (!start || !end) return false
    const key = dayKey(day)
    return key >= dayKey(start) && key <= dayKey(end)
}

export const WalkScheduleChart = (props: Props) => {
    const {
        days,
        dayWidth,
        services,
        workordersByVendorDate,
        transferStart,
        transferEnd,
        destReady,
        dtReady,
        dayOffKeys,
        zone,
        compact = false,
        onDayClick,
        onVendorRange,
    } = props
    const classes = useWalkScheduleStyles()
    const bodyRef = useRef<HTMLDivElement>(null)
    const dragRef = useRef<DragState | null>(null)
    const listenersRef = useRef<(() => void) | null>(null)
    const [drag, setDrag] = useState<DragState | null>(null)
    const [datePicker, setDatePicker] = useState<DatePickerState | null>(null)

    const chartWidth = days.length * dayWidth
    const serviceRowHeight = compact
        ? FIT_SERVICE_ROW_HEIGHT
        : SERVICE_ROW_HEIGHT
    const bodyHeight = services.reduce(
        (sum, service) =>
            sum +
            serviceRowHeight +
            service.template_service_vendors.length * VENDOR_ROW_HEIGHT,
        0,
    )

    const indexFromClientX = (clientX: number) => {
        const rect = bodyRef.current?.getBoundingClientRect()
        if (!rect || dayWidth <= 0) return 0
        const index = Math.floor((clientX - rect.left) / dayWidth)
        return Math.max(0, Math.min(days.length - 1, index))
    }

    const indexForIso = (value: string, edge: 'start' | 'end') => {
        const firstDay = days[0]
        const fallback = edge === 'start' ? 0 : Math.max(0, days.length - 1)
        if (!value || !firstDay) return fallback
        const parsed = DateTime.fromISO(value).setZone(zone)
        if (!parsed.isValid) return fallback
        const key = parsed.toFormat('yyyy-LL-dd')
        const index = days.findIndex((day) => dayKey(day) === key)
        if (index >= 0) return index
        return key < dayKey(firstDay) ? 0 : days.length - 1
    }

    const vendorIndexes = (startValue: string, endValue: string) => {
        const startIndex = indexForIso(startValue, 'start')
        const endIndex = Math.max(startIndex, indexForIso(endValue, 'end'))
        return { startIndex, endIndex }
    }

    const beginDrag = (
        event: React.MouseEvent,
        vendorId: number,
        edge: 'start' | 'end',
        startIndex: number,
        endIndex: number,
    ) => {
        event.preventDefault()
        event.stopPropagation()
        const initial = { vendorId, edge, startIndex, endIndex }
        dragRef.current = initial
        setDrag(initial)

        const move = (moveEvent: MouseEvent) => {
            const current = dragRef.current
            if (!current) return
            const index = indexFromClientX(moveEvent.clientX)
            const next =
                current.edge === 'start'
                    ? {
                          ...current,
                          startIndex: Math.min(index, current.endIndex),
                      }
                    : {
                          ...current,
                          endIndex: Math.max(index, current.startIndex),
                      }
            dragRef.current = next
            setDrag(next)
        }
        const up = () => {
            const finalDrag = dragRef.current
            dragRef.current = null
            setDrag(null)
            window.removeEventListener('mousemove', move)
            window.removeEventListener('mouseup', up)
            listenersRef.current = null
            if (!finalDrag) return
            if (
                finalDrag.startIndex === startIndex &&
                finalDrag.endIndex === endIndex
            ) {
                return
            }
            onVendorRange(
                finalDrag.vendorId,
                finalDrag.startIndex,
                finalDrag.endIndex,
            )
        }
        listenersRef.current = () => {
            window.removeEventListener('mousemove', move)
            window.removeEventListener('mouseup', up)
        }
        window.addEventListener('mousemove', move)
        window.addEventListener('mouseup', up)
    }

    useEffect(() => () => listenersRef.current?.(), [])

    let cursor = 0
    const rows = services.flatMap((service) => {
        const serviceTop = cursor
        cursor += serviceRowHeight
        const vendors = service.template_service_vendors.map((vendor) => {
            const top = cursor
            cursor += VENDOR_ROW_HEIGHT
            const persisted = vendorIndexes(vendor.start_date, vendor.end_date)
            const active =
                drag?.vendorId === vendor.id
                    ? { startIndex: drag.startIndex, endIndex: drag.endIndex }
                    : persisted
            return { vendor, top, ...active, color: service.service.color }
        })
        return [
            { kind: 'service' as const, top: serviceTop },
            ...vendors.map((row) => ({ kind: 'vendor' as const, ...row })),
        ]
    })

    return (
        <div className={classes.chart} style={{ width: chartWidth }}>
            <div className={classes.axis} style={{ width: chartWidth }}>
                {days.map((day) => {
                    const key = dayKey(day)
                    const off = dayOffKeys.has(key)
                    const dest = destReady != null && dayKey(destReady) === key
                    const dt = dtReady != null && dayKey(dtReady) === key
                    return (
                        <button
                            key={key}
                            type="button"
                            className={classes.dayHeader}
                            style={{
                                width: dayWidth,
                                background: off
                                    ? 'repeating-linear-gradient(-45deg, transparent, transparent 4px, rgba(0,0,0,0.06) 4px, rgba(0,0,0,0.06) 8px)'
                                    : undefined,
                            }}
                            onClick={(event) =>
                                onDayClick(day, event.currentTarget)
                            }
                        >
                            {dayWidth >= 48 && (
                                <span className={classes.dayWeek}>
                                    {day.toFormat('ccc')}
                                </span>
                            )}
                            <span className={classes.dayNumber}>
                                {day.toFormat('d')}
                            </span>
                            <span className={classes.dayDots}>
                                {dest && (
                                    <span
                                        title="Destination unit ready"
                                        style={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: 3,
                                            background: '#ef6c00',
                                        }}
                                    />
                                )}
                                {dt && (
                                    <span
                                        title="Double transfer unit ready"
                                        style={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: 3,
                                            background: '#e65100',
                                        }}
                                    />
                                )}
                            </span>
                        </button>
                    )
                })}
            </div>
            <div
                ref={bodyRef}
                className={classes.chartBody}
                style={{ width: chartWidth, height: bodyHeight }}
            >
                {days.map((day, index) => {
                    const key = dayKey(day)
                    const off = dayOffKeys.has(key)
                    const inTransfer = covers(day, transferStart, transferEnd)
                    return (
                        <div
                            key={key}
                            className={classes.column}
                            style={{
                                left: index * dayWidth,
                                width: dayWidth,
                                background: off
                                    ? 'repeating-linear-gradient(-45deg, transparent, transparent 4px, rgba(0,0,0,0.05) 4px, rgba(0,0,0,0.05) 8px)'
                                    : inTransfer
                                    ? 'rgba(46, 125, 50, 0.08)'
                                    : undefined,
                            }}
                        />
                    )
                })}
                {rows.map((row) => {
                    if (row.kind === 'service') {
                        return (
                            <div
                                key={`service-${row.top}`}
                                className={classes.serviceSpacer}
                                style={{
                                    position: 'absolute',
                                    top: row.top,
                                    left: 0,
                                    right: 0,
                                    height: serviceRowHeight,
                                }}
                            />
                        )
                    }
                    const left = row.startIndex * dayWidth
                    const width = (row.endIndex - row.startIndex + 1) * dayWidth
                    return (
                        <div
                            key={row.vendor.id}
                            className={classes.vendorSpacer}
                            style={{
                                position: 'absolute',
                                top: row.top,
                                left: 0,
                                right: 0,
                            }}
                        >
                            <div
                                className={classes.bar}
                                style={{
                                    top: 10,
                                    height: 24,
                                    left,
                                    width,
                                    background: row.color || '#46b083',
                                }}
                            >
                                {days.map((day, index) => {
                                    if (
                                        index < row.startIndex ||
                                        index > row.endIndex
                                    ) {
                                        return null
                                    }
                                    const count = workordersByVendorDate.get(
                                        `${row.vendor.id}-${dayKey(day)}`,
                                    )
                                    if (!count) return null
                                    return (
                                        <div
                                            key={dayKey(day)}
                                            className={classes.workFill}
                                            style={{
                                                left:
                                                    (index - row.startIndex) *
                                                    dayWidth,
                                                width: dayWidth,
                                            }}
                                        >
                                            {dayWidth >= 28 ? count : ''}
                                        </div>
                                    )
                                })}
                                <div
                                    className={classes.barHandle}
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                    }}
                                    onMouseDown={(event) =>
                                        beginDrag(
                                            event,
                                            row.vendor.id,
                                            'start',
                                            row.startIndex,
                                            row.endIndex,
                                        )
                                    }
                                />
                                <div
                                    className={classes.barHandle}
                                    style={{
                                        position: 'absolute',
                                        right: 0,
                                        top: 0,
                                        bottom: 0,
                                    }}
                                    onMouseDown={(event) =>
                                        beginDrag(
                                            event,
                                            row.vendor.id,
                                            'end',
                                            row.startIndex,
                                            row.endIndex,
                                        )
                                    }
                                />
                                <Tooltip title="Set start date">
                                    <button
                                        type="button"
                                        className={classes.barDateButton}
                                        aria-label="Set start date"
                                        style={{ left: -6 }}
                                        onMouseDown={(event) =>
                                            event.stopPropagation()
                                        }
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            setDatePicker({
                                                vendorId: row.vendor.id,
                                                edge: 'start',
                                                anchor: event.currentTarget,
                                                startIndex: row.startIndex,
                                                endIndex: row.endIndex,
                                            })
                                        }}
                                    >
                                        <EventIcon style={{ fontSize: 12 }} />
                                    </button>
                                </Tooltip>
                                <Tooltip title="Set end date">
                                    <button
                                        type="button"
                                        className={classes.barDateButton}
                                        aria-label="Set end date"
                                        style={{ right: -6 }}
                                        onMouseDown={(event) =>
                                            event.stopPropagation()
                                        }
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            setDatePicker({
                                                vendorId: row.vendor.id,
                                                edge: 'end',
                                                anchor: event.currentTarget,
                                                startIndex: row.startIndex,
                                                endIndex: row.endIndex,
                                            })
                                        }}
                                    >
                                        <EventIcon style={{ fontSize: 12 }} />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                    )
                })}
            </div>
            {datePicker &&
                (() => {
                    const valueDay =
                        days[
                            datePicker.edge === 'start'
                                ? datePicker.startIndex
                                : datePicker.endIndex
                        ]
                    const minDay =
                        days[
                            datePicker.edge === 'start'
                                ? 0
                                : datePicker.startIndex
                        ]
                    const maxDay =
                        days[
                            datePicker.edge === 'start'
                                ? datePicker.endIndex
                                : days.length - 1
                        ]
                    if (!valueDay || !minDay || !maxDay) return null
                    const toPickerDate = (day: DateTime) =>
                        day.setZone('local', { keepLocalTime: true }).toJSDate()
                    return (
                        <Popover
                            open
                            anchorEl={datePicker.anchor}
                            onClose={() => setDatePicker(null)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                            }}
                        >
                            <DatePicker
                                autoOk
                                variant="static"
                                disableToolbar
                                value={toPickerDate(valueDay)}
                                minDate={toPickerDate(minDay)}
                                maxDate={toPickerDate(maxDay)}
                                onChange={(value) => {
                                    if (!(value instanceof Date)) return
                                    const picked = DateTime.fromJSDate(
                                        value,
                                    ).setZone(zone, { keepLocalTime: true })
                                    const index = days.findIndex(
                                        (day) =>
                                            dayKey(day) ===
                                            picked.toFormat('yyyy-LL-dd'),
                                    )
                                    if (index < 0) return
                                    const currentIndex =
                                        datePicker.edge === 'start'
                                            ? datePicker.startIndex
                                            : datePicker.endIndex
                                    if (index === currentIndex) return
                                    const startIndex =
                                        datePicker.edge === 'start'
                                            ? Math.min(
                                                  index,
                                                  datePicker.endIndex,
                                              )
                                            : datePicker.startIndex
                                    const endIndex =
                                        datePicker.edge === 'end'
                                            ? Math.max(
                                                  index,
                                                  datePicker.startIndex,
                                              )
                                            : datePicker.endIndex
                                    onVendorRange(
                                        datePicker.vendorId,
                                        startIndex,
                                        endIndex,
                                    )
                                    setDatePicker(null)
                                }}
                            />
                        </Popover>
                    )
                })()}
        </div>
    )
}
